import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { lotesAPI } from '../lib/api';
import { formatNumber, formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Scales,
  Clipboard,
  Receipt,
  ArrowLeft,
  CheckCircle,
  Warning,
  XCircle,
  TrendUp,
  TrendDown,
  Minus,
  Grains,
} from '@phosphor-icons/react';

export default function ComparacionAlimento() {
  const { loteId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    loadComparacion();
  }, [loteId]);

  const loadComparacion = async () => {
    setLoading(true);
    try {
      const response = await lotesAPI.getComparacionAlimento(loteId);
      setData(response.data);
    } catch (error) {
      toast.error('Error al cargar comparación');
    } finally {
      setLoading(false);
    }
  };

  const getEstadoConfig = (estado, diferencia) => {
    if (estado === 'ok') {
      return {
        icon: CheckCircle,
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        label: 'Coincide',
        description: 'El consumo registrado coincide con las facturas'
      };
    } else if (estado === 'exceso') {
      return {
        icon: TrendUp,
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        label: 'Exceso en Facturas',
        description: `Las facturas muestran ${Math.abs(diferencia).toFixed(2)} QQs más que el record`
      };
    } else {
      return {
        icon: TrendDown,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        label: 'Faltante en Facturas',
        description: `Faltan ${Math.abs(diferencia).toFixed(2)} QQs en las facturas vs el record`
      };
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4 p-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <p>No se encontraron datos</p>
          <Link to="/lotes">
            <Button className="mt-4">Volver a Lotes</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const estadoConfig = getEstadoConfig(
    data.comparacion.estado,
    data.comparacion.diferencia_quintales
  );
  const EstadoIcon = estadoConfig.icon;

  const maxQuintales = Math.max(
    data.comparacion.record_quintales || 0,
    data.comparacion.facturas_quintales || 0,
    1
  );

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to={`/lotes/${loteId}`}>
              <Button variant="ghost" size="icon">
                <ArrowLeft size={20} />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Scales size={28} className="text-[#F69031]" />
                Comparación de Alimento
              </h1>
              <p className="text-muted-foreground">{data.lote_nombre}</p>
            </div>
          </div>
          <Badge variant="outline" className="text-sm">
            Tasa: RD$ {data.tasa_cambio}/US$
          </Badge>
        </div>

        {/* Estado General */}
        <Card className={`${estadoConfig.bgColor} ${estadoConfig.borderColor} border-2`}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${estadoConfig.bgColor}`}>
                <EstadoIcon size={40} className={estadoConfig.color} weight="fill" />
              </div>
              <div className="flex-1">
                <h2 className={`text-xl font-bold ${estadoConfig.color}`}>
                  {estadoConfig.label}
                </h2>
                <p className="text-muted-foreground">{estadoConfig.description}</p>
                <p className="text-sm mt-1">
                  Diferencia: {data.comparacion.porcentaje_diferencia > 0 ? '+' : ''}{data.comparacion.porcentaje_diferencia}%
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">{Math.abs(data.comparacion.diferencia_quintales).toFixed(2)}</p>
                <p className="text-sm text-muted-foreground">Quintales de diferencia</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Comparación Visual */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Record de Granja */}
          <Card className="border-blue-200 border-l-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clipboard size={20} className="text-blue-600" />
                Record de Granja
              </CardTitle>
              <CardDescription>Consumo registrado diariamente</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {data.record_granja.tiene_datos ? (
                <>
                  <div className="text-center py-4">
                    <p className="text-4xl font-bold text-blue-600">
                      {formatNumber(data.record_granja.total_quintales)}
                    </p>
                    <p className="text-muted-foreground">Quintales totales</p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progreso vs Facturas</span>
                      <span>{((data.record_granja.total_quintales / maxQuintales) * 100).toFixed(0)}%</span>
                    </div>
                    <Progress 
                      value={(data.record_granja.total_quintales / maxQuintales) * 100} 
                      className="h-3"
                    />
                  </div>

                  {data.record_granja.por_semana.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium mb-2">Desglose por Semana:</p>
                      <div className="grid grid-cols-4 gap-2">
                        {data.record_granja.por_semana.map((s) => (
                          <div key={s.semana} className="bg-blue-50 rounded p-2 text-center">
                            <p className="text-xs text-muted-foreground">Sem {s.semana}</p>
                            <p className="font-semibold text-sm">{s.cantidad.toFixed(1)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Clipboard size={40} className="mx-auto mb-2 opacity-30" />
                  <p>No hay datos en el Record de Granja</p>
                  <Link to={`/lotes/${loteId}/record`} className="text-blue-600 hover:underline text-sm">
                    Ir a registrar →
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Facturas */}
          <Card className="border-amber-200 border-l-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Receipt size={20} className="text-amber-600" />
                Facturas de Alimento
              </CardTitle>
              <CardDescription>Compras registradas en gastos</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {data.facturas.tiene_datos ? (
                <>
                  <div className="text-center py-4">
                    <p className="text-4xl font-bold text-amber-600">
                      {formatNumber(data.facturas.total_cantidad)}
                    </p>
                    <p className="text-muted-foreground">Quintales comprados</p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progreso vs Record</span>
                      <span>{((data.facturas.total_cantidad / maxQuintales) * 100).toFixed(0)}%</span>
                    </div>
                    <Progress 
                      value={(data.facturas.total_cantidad / maxQuintales) * 100} 
                      className="h-3 [&>div]:bg-amber-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <div className="bg-amber-50 rounded p-3 text-center">
                      <p className="text-xs text-muted-foreground">Total RD$</p>
                      <p className="font-semibold">{formatNumber(data.facturas.total_dop)}</p>
                    </div>
                    <div className="bg-amber-50 rounded p-3 text-center">
                      <p className="text-xs text-muted-foreground">Total US$</p>
                      <p className="font-semibold">{formatNumber(data.facturas.total_usd)}</p>
                    </div>
                  </div>

                  {data.facturas.detalle.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium mb-2">Últimas facturas:</p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {data.facturas.detalle.slice(0, 5).map((f, idx) => (
                          <div key={idx} className="flex justify-between items-center bg-muted/30 rounded p-2 text-sm">
                            <div>
                              <p className="font-medium">{f.factura_numero || f.descripcion}</p>
                              <p className="text-xs text-muted-foreground">{f.fecha}</p>
                            </div>
                            <div className="text-right">
                              {f.cantidad > 0 && (
                                <p className="font-medium">{f.cantidad} QQs</p>
                              )}
                              <p className="text-xs text-muted-foreground">
                                {f.moneda_original === 'USD' ? `$${formatNumber(f.total_usd)}` : `RD$ ${formatNumber(f.total_dop)}`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Receipt size={40} className="mx-auto mb-2 opacity-30" />
                  <p>No hay facturas de alimento registradas</p>
                  <Link to={`/gastos/nuevo?lote=${loteId}&categoria=alimento`} className="text-amber-600 hover:underline text-sm">
                    Registrar gasto →
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Nota informativa */}
        <Card className="bg-muted/30">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Grains size={24} className="text-muted-foreground mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">¿Cómo funciona esta comparación?</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Record de Granja:</strong> Suma el consumo de alimento registrado diariamente en cada semana.</li>
                  <li><strong>Facturas:</strong> Suma las cantidades (en quintales) de los gastos de categoría "Alimento".</li>
                  <li><strong>Diferencia menor al 5%:</strong> Se considera que los datos coinciden.</li>
                  <li>Si hay una diferencia significativa, puede indicar alimento sin facturar o consumo no registrado.</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
